import React from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Globe,
  ShieldCheck,
  Award,
  BookOpen
} from "lucide-react";
import Navbar from "../../components/Navbar";

const pecbTrainings = [
  {
    title: "PECB ISO Lead Auditor",
    icon: ShieldCheck,
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978",
    description:
      "Internationally recognized Lead Auditor training enabling professionals to audit management systems effectively in accordance with ISO standards.",
    points: [
      "Audit principles & techniques",
      "ISO conformity assessment",
      "Audit planning & execution",
      "Reporting & corrective actions"
    ]
  },
  {
    title: "PECB ISO Lead Implementer",
    icon: GraduationCap,
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d",
    description:
      "Designed for professionals responsible for implementing, maintaining, and improving management systems within organizations.",
    points: [
      "Management system framework",
      "Implementation roadmap",
      "Risk-based thinking",
      "Continual improvement"
    ]
  },
  {
    title: "PECB Foundation & Transition",
    icon: BookOpen,
    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644",
    description:
      "Foundation and transition trainings provide essential understanding of ISO standards and updates for professionals at all levels.",
    points: [
      "ISO standards overview",
      "Key clauses & requirements",
      "Version transition guidance",
      "Practical case studies"
    ]
  }
];

const PECB_Trainings = () => {
  return (
    <>
    <Navbar />
    <section className="bg-white py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            PECB Certified Professional Trainings
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions delivers official PECB-certified trainings empowering
            professionals with globally recognized credentials and practical skills.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {pecbTrainings.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -14 }}
                className="group bg-gray-50 rounded-3xl overflow-hidden shadow-xl border border-gray-100"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                    <Icon size={22} />
                    <span className="font-semibold">PECB</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">
                    {item.description}
                  </p>

                  <ul className="space-y-3 text-sm text-gray-700">
                    {item.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Award size={16} className="text-[var(--bg-primary)] mt-1" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 text-center"
        >
          <p className="text-gray-600 mb-6">
            Become a globally certified professional with PECB trainings.
          </p>
          <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all">
            <Globe size={18} /> Enroll in PECB Training
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default PECB_Trainings;
